import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';

import { Store } from '@ngrx/store';

import { AttachmentTypes } from '../../interfaces/IAttachment';
import { gifImageUrl, IGif } from '../../interfaces/IGif';
import { GiphyService } from '../../services/giphy.service';
import { sendMessage } from '../../store/chat.actions';
import { AppStateWithChat } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-giphy',
  templateUrl: './giphy.component.html',
  styleUrls: ['./giphy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GiphyModalComponent {
  private dialogRef = inject(MatDialogRef<GiphyModalComponent>);
  private store = inject(Store<AppStateWithChat>);
  private giphyService = inject(GiphyService);

  searchInput = viewChild<ElementRef<HTMLInputElement>>('search');

  isLoading = signal(false);
  gifs = signal<IGif[]>([]);
  page = signal(0);
  readonly size = 50;
  gifSelected = signal<IGif | null>(null);
  text = signal<string | null>(null);

  constructor() {
    this.getTrending();

    effect((onCleanup) => {
      const input = this.searchInput();
      if (!input) {
        return;
      }
      const subscription = fromEvent<KeyboardEvent>(input.nativeElement, 'keyup')
        .pipe(
          map((event: KeyboardEvent) => (event.target as HTMLInputElement).value),
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe((searchText: string) => {
          this.page.set(1);
          this.gifs.set([]);
          this.gifSelected.set(null);
          if (searchText) {
            this.text.set(searchText);
            this.getSearch();
          } else {
            this.text.set(null);
            this.getTrending();
          }
        });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  getTrending(): void {
    this.isLoading.set(true);
    this.giphyService.getTrendingGifs(this.page(), this.size).subscribe({
      next: (response) => {
        this.appendGifs(response.data);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  getSearch(): void {
    const query = this.text();
    if (!query) {
      return;
    }
    this.isLoading.set(true);
    this.giphyService.getSearchByQueryGifs(query, this.page(), this.size).subscribe({
      next: (response) => {
        this.appendGifs(response.data);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  sendResponse(): void {
    const gifSelected = this.gifSelected();
    const thumbnailURL = gifImageUrl(gifSelected);
    if (!gifSelected || !thumbnailURL) {
      return;
    }
    this.store.dispatch(
      sendMessage({
        message: null,
        attachments: [
          {
            title: gifSelected.title as string,
            type: AttachmentTypes.IMAGE,
            thumbnailURL,
            uid: gifSelected.id,
            file: undefined,
          },
        ],
      })
    );
    this.dialogRef.close();
  }

  onScroll(): void {
    this.page.update((page) => page + 1);
    if (this.text()) {
      this.getSearch();
    } else {
      this.getTrending();
    }
  }

  selectGif(gif: IGif): void {
    if (!gifImageUrl(gif)) {
      return;
    }
    this.gifSelected.set(gif);
  }

  gifUrl(gif: IGif | null | undefined): string {
    return gifImageUrl(gif);
  }

  private appendGifs(data: IGif[]): void {
    const usable = data.filter((gif) => gifImageUrl(gif));
    this.gifs.update((gifs) => [...gifs, ...usable]);
  }
}
